import {Box, Button, TextField} from "@mui/material";
import EmailInput from "auth/EmailInput";
import PasswordInput from "auth/PasswordInput";
import {
  useForgotPasswordResetMutation,
  useSendResetCodeMutation,
} from "auth/api";
import {AUTH_MODES, type AuthMode} from "auth/types";
import {validatePassword} from "auth/utils/validators";
import useForm, {type FieldConfig} from "hooks/useForm";
import {type FC, useEffect, useState} from "react";
import {handleMutation} from "services/http/utils";
import {handleInfo} from "services/notification/handlers";
import {
  noopValidator,
  validateEmail,
  validateRequiredString,
} from "utils/validators";

type ResetFormProps = {
  onSwitch: (m: AuthMode) => void;
};
const ResetForm: FC<ResetFormProps> = ({onSwitch}) => {
  const [timer, setTimer] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  const [sendCode, {loading: sendingCode}] = useSendResetCodeMutation();
  const [resetPass, {loading: resetting}] = useForgotPasswordResetMutation();

  const initialFields: Record<string, FieldConfig<any>> = {
    email: ["", v => validateEmail(v, true)],
    code: ["", validateRequiredString],
    password: ["", validatePassword],
    confirmPassword: ["", noopValidator],
  };

  const onResetPassSuccess = () => {
    setCodeSent(false);
    onSwitch(AUTH_MODES.LOGIN);
  };

  const {fields, handleSubmit, setFieldError} = useForm({
    fields: initialFields,
    onSubmit: async values => {
      if (values.password !== values.confirmPassword) {
        setFieldError("confirmPassword", "Passwords do not match");
        return;
      }

      const payload = {
        email: values.email,
        code: values.code,
        password: values.password,
      };
      await handleMutation(
        resetPass,
        payload,
        "Password reset successfully! Please sign in.",
        undefined,
        {
          onSuccess: () => onResetPassSuccess(),
        },
      );
    },
  });

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    if (timer > 0) {
      interval = setInterval(() => setTimer(prev => prev - 1), 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer]);

  const onGetCodeSuccess = () => {
    setCodeSent(true);
    setTimer(60);
    handleInfo("Code sent! Valid for 10 minutes.");
  };

  const handleGetCode = async () => {
    const emailVal = fields.email.value;

    const payload = {email: emailVal};
    await handleMutation(sendCode, payload, undefined, undefined, {
      onSuccess: () => onGetCodeSuccess(),
    });
  };

  const getCodeDisabled =
    sendingCode || timer > 0 || !fields.email.value || !!fields.email.error;

  return (
    <form onSubmit={handleSubmit} style={{width: "100%", maxWidth: 400}}>
      <Box>
        <EmailInput {...fields.email} disabled={codeSent} size="small" />
        <Button
          variant="outlined"
          onClick={handleGetCode}
          disabled={getCodeDisabled}
          sx={{whiteSpace: "nowrap", mt: 1}}>
          {timer > 0 ? `Wait ${timer}s` : "Get Code"}
        </Button>
      </Box>

      {codeSent && (
        <>
          <TextField
            label="Verification Code"
            value={fields.code.value}
            onChange={e => fields.code.onChange(e.target.value)}
            onBlur={fields.code.onBlur}
            helperText={fields.code.error}
            error={!!fields.code.error}
            fullWidth
            margin="normal"
          />
          <PasswordInput label="New Password" {...fields.password} />
          <PasswordInput
            label="Confirm New Password"
            {...fields.confirmPassword}
          />
        </>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        loading={resetting}
        sx={{mt: 2}}
        disabled={!codeSent}>
        Update Password
      </Button>
    </form>
  );
};

export default ResetForm;
